import { useEffect, useState } from "react";
import { DEFAULT_KEYMAP, type Action, type InputManager, type KeyMap } from "../main/InputManager";

const LABELS: Record<Action, string> = {
  UP: "Up",
  DOWN: "Down",
  LEFT: "Left",
  RIGHT: "Right",
  A: "A",
  B: "B",
  START: "Start",
  SELECT: "Select",
  FAST_FORWARD: "Fast-forward (hold)",
};

/** Keyboard remapping, stored in localStorage by InputManager. */
export function KeymapEditor({ input, onClose }: { input: InputManager; onClose: () => void }) {
  const [map, setMap] = useState<KeyMap>(() => input.getKeymap());
  const [listening, setListening] = useState<Action | null>(null);

  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code !== "Escape") {
        const next = { ...map };
        for (const k of Object.keys(next) as Action[]) if (next[k] === e.code) next[k] = "";
        next[listening] = e.code;
        setMap(next);
        input.setKeymap(next);
      }
      setListening(null);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [listening, map, input]);

  return (
    <div className="card keymap" role="group" aria-label="Controls">
      <h3>Controls</h3>
      <table>
        <tbody>
          {(Object.keys(LABELS) as Action[]).map((a) => (
            <tr key={a}>
              <td>{LABELS[a]}</td>
              <td>
                <code>{map[a] || "—"}</code>
              </td>
              <td>
                <button onClick={() => setListening(a)}>{listening === a ? "Press a key…" : "Change"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="hint">Gamepads use the standard mapping. Soft reset is A+B+Start+Select.</p>
      <div className="row">
        <button
          onClick={() => {
            setMap({ ...DEFAULT_KEYMAP });
            input.setKeymap({ ...DEFAULT_KEYMAP });
          }}
        >
          Reset to defaults
        </button>
        <button onClick={onClose}>Done</button>
      </div>
    </div>
  );
}
